import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { authService } from '../services/auth.service';
import { legalConsentService } from '../services/legalConsent.service';
import { cookieConsentService } from '../services/cookieConsent.service';
import { marketingService } from '../services/marketing.service';
import type { LegalConsentStatus } from '../types/legalConsent.types';

// Authentication context using backend auth service
// Constitution Principle 3: AWS Cognito authentication (via backend)

interface AuthUser {
  userId: string;
  username: string;
  email?: string;
  phone?: string;
  role?: string;
  contractorId?: string;
}

interface SignInInput {
  username: string;
  password: string;
  captchaToken: string;
}

interface SignUpInput {
  username: string;
  password: string;
  email: string;
  phone_number: string;
  captchaToken: string;
  legalConsents?: {
    termsOfService: boolean;
    privacyPolicy: boolean;
  };
}

interface AuthContextType {
  user: AuthUser | null;
  legalConsentStatus: LegalConsentStatus | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  signIn: (input: SignInInput) => Promise<void>;
  signInWithGoogle: (idToken: string, referralCode?: string) => Promise<{ isNewAccount: boolean }>;
  signUp: (input: SignUpInput) => Promise<void>;
  confirmSignUp: (username: string, code: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
  refreshLegalConsentStatus: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [legalConsentStatus, setLegalConsentStatus] = useState<LegalConsentStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const checkAuthStatus = async () => {
    try {
      const userEmail = localStorage.getItem('userEmail');
      const storedUser = localStorage.getItem('user');
      
      if (userEmail && storedUser) {
        const userData = JSON.parse(storedUser);
        setUser({
          userId: userData.id?.toString() || '',
          username: userData.preferredName || userData.email,
          email: userData.email,
          phone: userData.phone,
          role: userData.role,
          contractorId: userData.contractorId,
        });
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      await checkAuthStatus();
      await refreshLegalConsentStatus();
    })();
  }, []);

  const refreshLegalConsentStatus = async () => {
    try {
      if (!localStorage.getItem('user')) {
        setLegalConsentStatus(null);
        return;
      }
      const status = await legalConsentService.getStatus();
      setLegalConsentStatus(status);
    } catch {
      setLegalConsentStatus(null);
    }
  };

  const syncCookieConsent = async () => {
    const [local, profile] = await Promise.all([
      Promise.resolve(cookieConsentService.getPreference()),
      cookieConsentService.fetchProfileConsent(),
    ]);

    if (profile?.marketingConsent !== null && profile?.marketingConsent !== undefined && profile?.cookiePolicyVersion) {
      // Profile has a recorded preference — it wins. Overwrite localStorage.
      cookieConsentService.setPreference(profile.marketingConsent, profile.cookiePolicyVersion);
      marketingService.init(profile.marketingConsent);
    } else if (local) {
      // Profile has no preference yet — push localStorage to profile.
      cookieConsentService.syncWithProfile(local.marketing, local.version);
      marketingService.init(local.marketing);
    }
  };

  const handleSignIn = async (input: SignInInput) => {
    try {
      await authService.login(input.username, input.password, input.captchaToken);
      await checkAuthStatus();
      await refreshLegalConsentStatus();
      await syncCookieConsent();
    } catch (error) {
      console.error('Sign in error:', error);
      throw error;
    }
  };

  const handleSignInWithGoogle = async (idToken: string, referralCode?: string) => {
    try {
      const result = await authService.loginWithGoogle(idToken, referralCode);
      await checkAuthStatus();
      await refreshLegalConsentStatus();
      await syncCookieConsent();
      return { isNewAccount: result.isNewAccount };
    } catch (error) {
      console.error('Google sign in error:', error);
      throw error;
    }
  };

  const handleSignUp = async (input: SignUpInput) => {
    try {
      await authService.register({
        email: input.email,
        password: input.password,
        phone: input.phone_number,
        name: input.username,
        captchaToken: input.captchaToken,
        legalConsents: {
          termsOfService: input.legalConsents?.termsOfService ?? false,
          privacyPolicy: input.legalConsents?.privacyPolicy ?? false,
        },
      });
    } catch (error) {
      console.error('Sign up error:', error);
      throw error;
    }
  };

  const handleConfirmSignUp = async (username: string, code: string) => {
    try {
      await authService.confirmSignUp(username, code);
    } catch (error) {
      console.error('Confirm sign up error:', error);
      throw error;
    }
  };

  const handleSignOut = async () => {
    try {
      await authService.logout();
      setUser(null);
      setLegalConsentStatus(null);
    } catch (error) {
      console.error('Sign out error:', error);
      // Clear localStorage anyway
      localStorage.removeItem('userEmail');
      localStorage.removeItem('user');
      setUser(null);
      setLegalConsentStatus(null);
    }
  };

  const refreshSession = async () => {
    await checkAuthStatus();
    await refreshLegalConsentStatus();
  };

  const value: AuthContextType = {
    user,
    legalConsentStatus,
    isLoading,
    isAuthenticated: !!user,
    signIn: handleSignIn,
    signInWithGoogle: handleSignInWithGoogle,
    signUp: handleSignUp,
    confirmSignUp: handleConfirmSignUp,
    signOut: handleSignOut,
    refreshSession,
    refreshLegalConsentStatus,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
